<app-image-cropper
  #coverPhotoCropper
  [cropMode]="'banner'"
  [title]="'settings.project_details_form.cropper_title' | translateLang"
  [hintText]="'settings.project_details_form.cropper_hint' | translateLang"
  [uploadingText]="'settings.project_details_form.cropper_uploading' | translateLang"
  (imageCroppedBlob)="onCoverPhotoCroppedBlob($event)">
</app-image-cropper>

<div class="project-form">
  <!-- Mobile: Header with title, stepper, and close button -->
  <div class="project-form__mobile-header">
    <div class="project-form__mobile-header-content">
      <h2 class="project-form__mobile-title">{{ currentStepLabelKey | translate }}</h2>

      <!-- Horizontal Stepper -->
      <div class="project-form__mobile-stepper">
        @for (stepItem of steps; track stepItem.step; let i = $index; let last = $last) {
          <div class="project-form__mobile-step-wrapper">
            <div class="project-form__mobile-step-icon"
                 [class.project-form__mobile-step-icon--active]="isStepActive(stepItem.step)"
                 [class.project-form__mobile-step-icon--completed]="isStepCompleted(stepItem.step) || hasStepData(stepItem.step)"
                 [class.project-form__mobile-step-icon--disabled]="isStepDisabled(stepItem.step)"
                 (click)="goToStep(stepItem.step)">
              <span class="project-form__mobile-step-number">{{ stepItem.number }}</span>
            </div>
            @if (!last) {
              <div class="project-form__mobile-step-line"
                   [class.project-form__mobile-step-line--completed]="isStepCompleted(stepItem.step) || hasStepData(stepItem.step)"
                   [class.project-form__mobile-step-line--next]="isStepActive(steps[i + 1]?.step)"></div>
            }
          </div>
        }
      </div>
    </div>

    <button class="project-form__close project-form__close--mobile"
            type="button"
            (click)="onCancel()"
            [attr.aria-label]="'settings.project_details_form.close_aria_label' | translate">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
  </div>

  <div class="project-form__container">
    <!-- Left Sidebar -->
    <div class="project-form__sidebar">
      <div class="project-form__stepper">
        @for (stepItem of steps; track stepItem.step) {
          <div class="project-form__step"
               [class.project-form__step--active]="isStepActive(stepItem.step)"
               [class.project-form__step--completed]="isStepCompleted(stepItem.step)"
               [class.project-form__step--has-data]="hasStepData(stepItem.step)"
               [class.project-form__step--disabled]="isStepDisabled(stepItem.step)"
               (click)="goToStep(stepItem.step)">
            <div class="project-form__step-icon">
              <span class="project-form__step-number">{{ stepItem.number }}</span>
            </div>
            <span class="project-form__step-label">{{ stepItem.labelKey | translate }}</span>
          </div>
        }
      </div>

      <!-- Sidebar Action Buttons -->
      <div class="project-form__sidebar-actions">
        <div class="project-form__sidebar-buttons">
          @if (showSaveAsDraftButton) {
            <button
              class="project-form__button project-form__button--draft"
              type="button"
              [disabled]="isSaving"
              (click)="onSaveAsDraft()">
              {{ isSaving ? ('settings.project_details_form.saving' | translate) : ('settings.project_details_form.save_as_draft' | translate) }}
            </button>
          }
          <button
            class="project-form__button project-form__button--submit"
            type="button"
            [disabled]="isSubmitting || isCharityBlocked"
            (click)="onSubmit()">
            {{ isSubmitting ? ('settings.project_details_form.submitting' | translate) : ('settings.project_details_form.submit' | translate) }}
          </button>
        </div>
        @if (charityBlockedCase) {
          <div class="project-form__charity-blocked-hint">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" class="project-form__charity-blocked-hint-icon">
              <g clip-path="url(#clip0_charity_blocked_hint)">
                <path d="M6.5 12.25C9.67564 12.25 12.25 9.67564 12.25 6.5C12.25 3.32436 9.67564 0.75 6.5 0.75C3.32436 0.75 0.75 3.32436 0.75 6.5C0.75 9.67564 3.32436 12.25 6.5 12.25Z" stroke="#E91A1A" stroke-width="1.2"/>
                <path d="M6.5 5.5V9" stroke="#E91A1A" stroke-width="1.2" stroke-linecap="round"/>
                <path d="M6.50039 4.79998C6.83176 4.79998 7.10039 4.53135 7.10039 4.19998C7.10039 3.8686 6.83176 3.59998 6.50039 3.59998C6.16902 3.59998 5.90039 3.8686 5.90039 4.19998C5.90039 4.53135 6.16902 4.79998 6.50039 4.79998Z" fill="#E91A1A"/>
              </g>
              <defs>
                <clipPath id="clip0_charity_blocked_hint">
                  <rect width="13" height="13" fill="white"/>
                </clipPath>
              </defs>
            </svg>
            <span [innerHTML]="'settings.project_details_form.charity_not_live_submit_hint' | translate"></span>
          </div>
        }
      </div>
    </div>

    <!-- Right Content Area -->
    <div class="project-form__content">
      <!-- Header Bar -->
      <div class="project-form__header">
        <h1 class="project-form__title">{{ steps[currentStep]?.labelKey | translate }}</h1>
        <button class="project-form__close project-form__close--desktop"
                type="button"
                (click)="onCancel()"
                [attr.aria-label]="'settings.project_details_form.close_aria_label' | translate">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>

      <!-- Scrollable Form Body -->
      <div class="project-form__body">
        <!-- Loading State -->
        @if (isLoadingDropdowns) {
          <div class="project-form__loading">
            <div class="project-form__spinner"></div>
            <p>{{ 'settings.project_details_form.loading_form' | translate }}</p>
          </div>
        }

        <!-- Details Step -->
        @if (!isLoadingDropdowns && currentStep === ProjectFormStep.DETAILS) {
          <form [formGroup]="detailsForm" class="project-form__form">

            <!-- ── Project Info Section ── -->
            <div class="project-form__section" formGroupName="projectInfo">
              <!-- Project Profile -->
              @if (!isCharityAdmin) {
                <div class="project-form__field">
                  <label class="project-form__label" for="projectProfile">
                    {{ 'settings.project_details_form.project_profile_label' | translate }}<span class="project-form__required">*</span>
                  </label>
                  <select
                    id="projectProfile"
                    class="project-form__select"
                    [class.project-form__input--error]="detailsForm.get('projectInfo.projectProfile')?.invalid && detailsForm.get('projectInfo.projectProfile')?.touched"
                    formControlName="projectProfile"
                    (change)="onProjectProfileChange()">
                    <option [ngValue]="null">{{ 'settings.project_details_form.select_profile' | translate }}</option>
                    @for (profile of projectProfiles; track profile.uuid) {
                      <option [ngValue]="profile.uuid">{{ profile.name }}</option>
                    }
                  </select>
                  @if (detailsForm.get('projectInfo.projectProfile')?.hasError('required') && detailsForm.get('projectInfo.projectProfile')?.touched) {
                    <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                  }
                </div>
              }

              @if (!data.organisationUuid || isCharityAdmin) {
              <!-- Select Charity -->
              <div class="project-form__field">
                <label class="project-form__label">
                  {{ 'settings.project_details_form.select_charity_label' | translate }}<span class="project-form__required">*</span>
                </label>
                <app-select
                  [options]="charityOptions"
                  [value]="detailsForm.get('projectInfo.charityUuid')?.value"
                  [searchable]="true"
                  [disabled]="isCharitySelectionDisabled"
                  [placeholder]="'settings.project_details_form.select_charity_placeholder' | translate"
                  (valueChange)="onCharityChange($event)">
                </app-select>
                @if (detailsForm.get('projectInfo.charityUuid')?.hasError('required') && detailsForm.get('projectInfo.charityUuid')?.touched) {
                  <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                }
                @if (charityBlockedCase) {
                  <div class="project-form__charity-blocked-message">
                    {{ (charityBlockedCase === 'A' ? 'settings.project_details_form.charity_not_live_case_a' : 'settings.project_details_form.charity_not_live_case_b') | translate: { charityName: selectedCharity?.name } }}
                  </div>
                }
              </div>
              }
            </div>

            <!-- ── Project Details Section ── -->
            <div class="project-form__section" formGroupName="projectDetails">
              <div class="project-form__section-header">
                <h3 class="project-form__section-title">
                  {{ 'settings.project_details_form.project_details_title' | translate }}
                </h3>
                <p class="project-form__section-description">
                  {{ 'settings.project_details_form.project_details_description' | translate }}
                </p>
              </div>

              <!-- Project Name (full width) -->
              <div class="project-form__field">
                <div class="project-form__field-header">
                  <label class="project-form__label" for="projectName">
                    {{ 'settings.project_details_form.project_name_label' | translate }}<span class="project-form__required">*</span>
                  </label>
                </div>
                <input
                  type="text"
                  id="projectName"
                  class="project-form__input"
                  formControlName="projectName"
                  [class.project-form__input--error]="detailsForm.get('projectDetails.projectName')?.invalid && detailsForm.get('projectDetails.projectName')?.touched"
                  [placeholder]="'settings.project_details_form.project_name_placeholder' | translate"
                  maxlength="200">
                @if (detailsForm.get('projectDetails.projectName')?.hasError('required') && detailsForm.get('projectDetails.projectName')?.touched) {
                  <div class="project-form__error">
                    {{ 'settings.project_details_form.required_field' | translate }}
                  </div>
                }
                @if (detailsForm.get('projectDetails.projectName')?.hasError('duplicateName')) {
                  <div class="project-form__error">
                    {{ 'settings.project_details_form.duplicate_name_error' | translate }}
                  </div>
                }
              </div>

              <!-- Giving Link (full width) -->
              @if (isFieldVisible('givingLink')) {
              <div class="project-form__field" [formGroup]="projectTimelineGroup">
                <label class="project-form__label" for="givingLink">
                  {{ 'settings.project_details_form.giving_link_label' | translate }}@if (isFieldRequired('givingLink')) { <span class="project-form__required">*</span> }
                </label>
                <!-- Editable only by Foundation Admin / Super Admin: the control is
                     disabled in applyGivingLinkPermission() (which renders the `disabled`
                     attribute) and readonly here; buildGivingLinkPayloadValue() re-asserts
                     the server value on submit. -->
                <input
                  type="url"
                  id="givingLink"
                  class="project-form__input"
                  formControlName="givingLink"
                  [readonly]="!canEditGivingLink"
                  [class.project-form__input--error]="detailsForm.get('projectTimeline.givingLink')?.invalid && detailsForm.get('projectTimeline.givingLink')?.touched"
                  [placeholder]="'settings.project_details_form.giving_link_placeholder' | translate">
                @if (detailsForm.get('projectTimeline.givingLink')?.hasError('required') && detailsForm.get('projectTimeline.givingLink')?.touched) {
                  <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                }
              </div>
              } @else if (charityBlockedCase) {
                <!-- The charity is locked in and its selector is hidden (e.g. Foundation/Super
                     Admin via a charity's own "Add Project" button), but the block still applies —
                     show the same message on its own so it isn't silently dropped. -->
                <div class="project-form__field">
                  <div class="project-form__charity-blocked-message">
                    {{ (charityBlockedCase === 'A' ? 'settings.project_details_form.charity_not_live_case_a' : 'settings.project_details_form.charity_not_live_case_b') | translate: { charityName: selectedCharity?.name } }}
                  </div>
                </div>
              }

              <!-- Cause (full width) -->
              @if (isFieldVisible('cause')) {
              <div class="project-form__field">
                <label class="project-form__label" for="cause">
                  {{ 'settings.project_details_form.project_cause_label' | translate }}@if (isFieldRequired('cause')) { <span class="project-form__required">*</span> }
                </label>
                <div class="project-form__cause-select"
                     (click)="$event.stopPropagation()">
                  <div class="project-form__cause-select__trigger"
                       [class.project-form__cause-select__trigger--placeholder]="!selectedCauseName"
                       [class.project-form__cause-select__trigger--error]="detailsForm.get('projectDetails.cause')?.invalid && detailsForm.get('projectDetails.cause')?.touched"
                       (click)="toggleCauseDropdown()">
                    {{ selectedCauseName || ('settings.project_details_form.project_cause_placeholder' | translate) }}
                  </div>
                  @if (isCauseDropdownOpen) {
                    <div class="project-form__cause-select__list">
                      @for (cause of causes; track cause.uuid) {
                        <div class="project-form__cause-select__option"
                             [class.project-form__cause-select__option--selected]="cause.uuid === detailsForm.get('projectDetails.cause')?.value"
                             (click)="onCauseSelected(cause.uuid)">
                          {{ cause.name }}
                        </div>
                      }
                    </div>
                  }
                  @if (detailsForm.get('projectDetails.cause')?.hasError('required') && detailsForm.get('projectDetails.cause')?.touched) {
                    <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                  }
                </div>
              </div>
              }

              <!-- Website (full width) -->
              @if (isFieldVisible('website')) {
              <div class="project-form__field">
                <label class="project-form__label" for="website">
                  {{ 'settings.project_details_form.project_website_label' | translate }}@if (isFieldRequired('website')) { <span class="project-form__required">*</span> }
                </label>
                <input
                  type="url"
                  id="website"
                  class="project-form__input"
                  formControlName="website"
                  [class.project-form__input--error]="detailsForm.get('projectDetails.website')?.invalid && detailsForm.get('projectDetails.website')?.touched"
                  [placeholder]="'settings.project_details_form.project_website_placeholder' | translate">
                @if (detailsForm.get('projectDetails.website')?.hasError('required') && detailsForm.get('projectDetails.website')?.touched) {
                  <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                }
              </div>
              }

              <!-- Category (full width) -->
              @if (isFieldVisible('category')) {
              <div class="project-form__field">
                <label class="project-form__label">
                  {{ 'settings.project_details_form.project_category_label' | translate }}@if (isFieldRequired('category')) { <span class="project-form__required">*</span> }
                </label>
                <app-multi-select-dropdown
                  formControlName="category"
                  [options]="categories"
                  [placeholder]="'settings.project_details_form.project_category_placeholder' | translate"
                  [selectAllLabel]="'settings.project_details_form.select_all' | translate">
                </app-multi-select-dropdown>
                @if (detailsForm.get('projectDetails.category')?.hasError('required') && detailsForm.get('projectDetails.category')?.touched) {
                  <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                }
              </div>
              }

              <!-- Country (full width) -->
              @if (isFieldVisible('country')) {
              <div class="project-form__field">
                <label class="project-form__label">
                  {{ 'settings.project_details_form.project_country_label' | translate }}@if (isFieldRequired('country')) { <span class="project-form__required">*</span> }
                </label>
                <app-multi-select-dropdown
                  formControlName="country"
                  [options]="countries"
                  [placeholder]="'settings.project_details_form.project_country_placeholder' | translate"
                  [selectAllLabel]="'settings.project_details_form.select_all' | translate">
                </app-multi-select-dropdown>
                @if (detailsForm.get('projectDetails.country')?.hasError('required') && detailsForm.get('projectDetails.country')?.touched) {
                  <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                }
              </div>
              }

              <!-- Website (full width) -->
              @if (isFieldVisible('website')) {
              <div class="project-form__field">
                <label class="project-form__label" for="website">
                  {{ 'settings.project_details_form.project_website_label' | translate }}@if (isFieldRequired('website')) { <span class="project-form__required">*</span> }
                </label>
                <input
                  type="url"
                  id="website"
                  class="project-form__input"
                  formControlName="website"
                  [class.project-form__input--error]="detailsForm.get('projectDetails.website')?.invalid && detailsForm.get('projectDetails.website')?.touched"
                  [placeholder]="'settings.project_details_form.project_website_placeholder' | translate">
                @if (detailsForm.get('projectDetails.website')?.hasError('required') && detailsForm.get('projectDetails.website')?.touched) {
                  <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                }
              </div>
              }
            </div>

            <!-- ── Social Media Links Section ── -->
            @if (isSectionVisible('socialMedia')) {
            <div class="project-form__section" formGroupName="socialMedia">
              <div class="project-form__section-header">
                <h3 class="project-form__section-title">
                  {{ 'settings.project_details_form.social_media_title' | translate }}
                </h3>
                <p class="project-form__section-description">
                  {{ 'settings.project_details_form.social_media_description' | translate }}
                </p>
              </div>

              <!-- LinkedIn + X row -->
              <div class="project-form__field-row">
                @if (isFieldVisible('linkedin')) {
                <div class="project-form__field">
                  <label class="project-form__label" for="linkedin">
                    {{ 'settings.project_details_form.linkedin_label' | translate }}@if (isFieldRequired('linkedin')) { <span class="project-form__required">*</span> }
                  </label>
                  <input
                    type="url"
                    id="linkedin"
                    class="project-form__input"
                    formControlName="linkedin"
                    [class.project-form__input--error]="detailsForm.get('socialMedia.linkedin')?.invalid && detailsForm.get('socialMedia.linkedin')?.touched"
                    [placeholder]="'settings.project_details_form.linkedin_placeholder' | translate">
                  @if (detailsForm.get('socialMedia.linkedin')?.hasError('required') && detailsForm.get('socialMedia.linkedin')?.touched) {
                    <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                  }
                </div>
                }

                @if (isFieldVisible('twitter')) {
                <div class="project-form__field">
                  <label class="project-form__label" for="twitter">
                    {{ 'settings.project_details_form.x_label' | translate }}@if (isFieldRequired('twitter')) { <span class="project-form__required">*</span> }
                  </label>
                  <input
                    type="url"
                    id="twitter"
                    class="project-form__input"
                    formControlName="twitter"
                    [class.project-form__input--error]="detailsForm.get('socialMedia.twitter')?.invalid && detailsForm.get('socialMedia.twitter')?.touched"
                    [placeholder]="'settings.project_details_form.x_placeholder' | translate">
                  @if (detailsForm.get('socialMedia.twitter')?.hasError('required') && detailsForm.get('socialMedia.twitter')?.touched) {
                    <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                  }
                </div>
                }
              </div>

              <!-- Facebook + Instagram row -->
              <div class="project-form__field-row">
                @if (isFieldVisible('facebook')) {
                <div class="project-form__field">
                  <label class="project-form__label" for="facebook">
                    {{ 'settings.project_details_form.facebook_label' | translate }}@if (isFieldRequired('facebook')) { <span class="project-form__required">*</span> }
                  </label>
                  <input
                    type="url"
                    id="facebook"
                    class="project-form__input"
                    formControlName="facebook"
                    [class.project-form__input--error]="detailsForm.get('socialMedia.facebook')?.invalid && detailsForm.get('socialMedia.facebook')?.touched"
                    [placeholder]="'settings.project_details_form.facebook_placeholder' | translate">
                  @if (detailsForm.get('socialMedia.facebook')?.hasError('required') && detailsForm.get('socialMedia.facebook')?.touched) {
                    <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                  }
                </div>
                }

                @if (isFieldVisible('instagram')) {
                <div class="project-form__field">
                  <label class="project-form__label" for="instagram">
                    {{ 'settings.project_details_form.instagram_label' | translate }}@if (isFieldRequired('instagram')) { <span class="project-form__required">*</span> }
                  </label>
                  <input
                    type="url"
                    id="instagram"
                    class="project-form__input"
                    formControlName="instagram"
                    [class.project-form__input--error]="detailsForm.get('socialMedia.instagram')?.invalid && detailsForm.get('socialMedia.instagram')?.touched"
                    [placeholder]="'settings.project_details_form.instagram_placeholder' | translate">
                  @if (detailsForm.get('socialMedia.instagram')?.hasError('required') && detailsForm.get('socialMedia.instagram')?.touched) {
                    <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                  }
                </div>
                }
              </div>
            </div>
            }

            <!-- ── Organization Address / Timeline Section ── -->
            @if (isSectionVisible('projectTimeline')) {
            <div class="project-form__section" formGroupName="projectTimeline">
              <div class="project-form__section-header">
                <h3 class="project-form__section-title">
                  {{ 'settings.project_details_form.org_address_title' | translate }}
                </h3>
                <p class="project-form__section-description">
                  {{ 'settings.project_details_form.org_address_description' | translate }}
                </p>
              </div>

              <!-- Target Population (full width) -->
              @if (isFieldVisible('targetPopulation')) {
              <div class="project-form__field">
                <label class="project-form__label" for="targetPopulation">
                  {{ 'settings.project_details_form.target_population_label' | translate }}@if (isFieldRequired('targetPopulation')) { <span class="project-form__required">*</span> }
                </label>
                <input
                  type="text"
                  id="targetPopulation"
                  class="project-form__input"
                  formControlName="targetPopulation"
                  [class.project-form__input--error]="detailsForm.get('projectTimeline.targetPopulation')?.invalid && detailsForm.get('projectTimeline.targetPopulation')?.touched"
                  [placeholder]="'settings.project_details_form.target_population_placeholder' | translate">
                @if (detailsForm.get('projectTimeline.targetPopulation')?.hasError('required') && detailsForm.get('projectTimeline.targetPopulation')?.touched) {
                  <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                }
              </div>
              }

              <!-- Start Date + End Date row -->
              <div class="project-form__field-row">
                @if (isFieldVisible('startDate')) {
                <div class="project-form__field">
                  <label class="project-form__label" for="startDate">
                    {{ 'settings.project_details_form.start_date_label' | translate }}@if (isFieldRequired('startDate')) { <span class="project-form__required">*</span> }
                  </label>
                  <div class="project-form__date-wrapper" (click)="$any(startDatePicker).showPicker()">
                    <input
                      type="text"
                      id="startDate"
                      readonly
                      class="project-form__input"
                      [class.project-form__input--error]="detailsForm.get('projectTimeline.startDate')?.invalid && detailsForm.get('projectTimeline.startDate')?.touched"
                      [placeholder]="null | gsDatePattern"
                      [value]="detailsForm.get('projectTimeline.startDate')?.value | gsDateFormat">
                    <input
                      #startDatePicker
                      type="date"
                      class="project-form__date-picker"
                      formControlName="startDate"
                      max="9999-12-31">
                  </div>
                  @if (detailsForm.get('projectTimeline.startDate')?.hasError('required') && detailsForm.get('projectTimeline.startDate')?.touched) {
                    <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                  }
                </div>
                }

                @if (isFieldVisible('endDate')) {
                <div class="project-form__field">
                  <label class="project-form__label" for="endDate">
                    {{ 'settings.project_details_form.end_date_label' | translate }}@if (isFieldRequired('endDate')) { <span class="project-form__required">*</span> }
                  </label>
                  <div class="project-form__date-wrapper" (click)="$any(endDatePicker).showPicker()">
                    <input
                      type="text"
                      id="endDate"
                      readonly
                      class="project-form__input"
                      [class.project-form__input--error]="detailsForm.get('projectTimeline.endDate')?.invalid && detailsForm.get('projectTimeline.endDate')?.touched"
                      [placeholder]="null | gsDatePattern"
                      [value]="detailsForm.get('projectTimeline.endDate')?.value | gsDateFormat">
                    <input
                      #endDatePicker
                      type="date"
                      class="project-form__date-picker"
                      formControlName="endDate"
                      max="9999-12-31">
                  </div>
                  @if (detailsForm.get('projectTimeline.endDate')?.hasError('required') && detailsForm.get('projectTimeline.endDate')?.touched) {
                    <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                  }
                </div>
                }
              </div>

              <!-- Currency + Amount Raised + Total Budget row -->
              <div class="project-form__field-row project-form__field-row--three">
                @if (isFieldVisible('currency')) {
                <div class="project-form__field">
                  <label class="project-form__label" for="currency">
                    {{ 'settings.project_details_form.project_currency_label' | translate }}@if (isFieldRequired('currency')) { <span class="project-form__required">*</span> }
                  </label>
                  <select
                    id="currency"
                    class="project-form__select"
                    formControlName="currency"
                    [class.project-form__input--error]="detailsForm.get('projectTimeline.currency')?.invalid && detailsForm.get('projectTimeline.currency')?.touched">
                    <option [ngValue]="null">{{ 'settings.project_details_form.project_currency_placeholder' | translate }}</option>
                    @for (currency of currencies; track currency.uuid) {
                      <option [ngValue]="currency.uuid">{{ currency.name }}</option>
                    }
                  </select>
                  @if (detailsForm.get('projectTimeline.currency')?.hasError('required') && detailsForm.get('projectTimeline.currency')?.touched) {
                    <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                  }
                </div>
                }

                @if (isFieldVisible('amountRaised')) {
                <div class="project-form__field">
                  <label class="project-form__label" for="amountRaised">
                    {{ 'settings.project_details_form.amount_raised_label' | translate }}@if (isFieldRequired('amountRaised')) { <span class="project-form__required">*</span> }
                  </label>
                  <input
                    type="number"
                    id="amountRaised"
                    class="project-form__input"
                    formControlName="amountRaised"
                    min="0"
                    [class.project-form__input--error]="detailsForm.get('projectTimeline.amountRaised')?.invalid && detailsForm.get('projectTimeline.amountRaised')?.touched"
                    [placeholder]="'settings.project_details_form.amount_raised_placeholder' | translate"
                    (keydown)="onNumericKeyDown($event)">
                  @if (detailsForm.get('projectTimeline.amountRaised')?.hasError('required') && detailsForm.get('projectTimeline.amountRaised')?.touched) {
                    <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                  }
                </div>
                }

                @if (isFieldVisible('totalBudget')) {
                <div class="project-form__field">
                  <label class="project-form__label" for="totalBudget">
                    {{ 'settings.project_details_form.total_budget_label' | translate }}@if (isFieldRequired('totalBudget')) { <span class="project-form__required">*</span> }
                  </label>
                  <input
                    type="number"
                    id="totalBudget"
                    class="project-form__input"
                    formControlName="totalBudget"
                    min="0"
                    [class.project-form__input--error]="detailsForm.get('projectTimeline.totalBudget')?.invalid && detailsForm.get('projectTimeline.totalBudget')?.touched"
                    [placeholder]="'settings.project_details_form.total_budget_placeholder' | translate"
                    (keydown)="onNumericKeyDown($event)">
                  @if (detailsForm.get('projectTimeline.totalBudget')?.hasError('required') && detailsForm.get('projectTimeline.totalBudget')?.touched) {
                    <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                  }
                </div>
                }
              </div>
            </div>
            }

            <!-- ── Project Content Section ── -->
            @if (isSectionVisible('projectContent')) {
            <div class="project-form__section" formGroupName="projectContent">
              <div class="project-form__section-header">
                <h3 class="project-form__section-title">
                  {{ 'settings.project_details_form.project_content_title' | translate }}
                </h3>
                <p class="project-form__section-description">
                  {{ 'settings.project_details_form.project_content_description' | translate }}
                </p>
              </div>

              <!-- Project Video Upload -->
              @if (isFieldVisible('videoUrl')) {
              <div class="project-form__media-row">
                <div class="project-form__media-info">
                  <h4 class="project-form__media-title">
                    {{ 'settings.project_details_form.project_video_title' | translate }}@if (isFieldRequired('videoUrl')) { <span class="project-form__required">*</span> }
                  </h4>
                  <p class="project-form__media-description">
                    {{ 'settings.project_details_form.project_video_description' | translate }}
                  </p>
                </div>
                <div class="project-form__upload-area"
                     (click)="onUploadVideoClick()"
                     [class.project-form__upload-area--uploading]="isUploadingVideo">
                  <svg class="project-form__upload-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4V16M12 4L8 8M12 4L16 8M4 17V19C4 19.5304 4.21071 20.0391 4.58579 20.4142C4.96086 20.7893 5.46957 21 6 21H18C18.5304 21 19.0391 20.7893 19.4142 20.4142C19.7893 20.0391 20 19.5304 20 19V17" stroke="#606060" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span class="project-form__upload-text">
                    {{ isUploadingVideo ? ('settings.project_details_form.uploading' | translate) : ('settings.project_details_form.upload_video_label' | translate) }}
                  </span>
                  <span class="project-form__upload-formats">
                    {{ 'settings.project_details_form.upload_video_formats' | translate }}
                  </span>
                </div>
              </div>

              <input
                type="file"
                id="videoFileInput"
                accept=".mp4,.mov,.avi,.mpeg,.ogv,.webm,.3gp,video/mp4,video/quicktime,video/avi,video/mpeg,video/ogg,video/webm,video/3gpp"
                style="display: none;"
                (change)="onVideoFileSelected($event)">

              @if (uploadedVideo) {
                <div class="project-form__file-preview">
                  <div class="project-form__file-preview-content">
                    <svg class="project-form__file-preview-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" stroke="#606060" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M13 2V9H20" stroke="#606060" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span
                      class="project-form__file-preview-name"
                      [customTooltip]="uploadedVideo.fileName"
                      [tooltipDisabled]="uploadedVideo.fileName.length <= 80">
                      {{ uploadedVideo.fileName | truncateFilename }}
                    </span>
                  </div>
                  <div class="project-form__file-preview-actions">
                    <button type="button" class="project-form__file-preview-button project-form__file-preview-button--preview" (click)="previewVideo()">
                      Preview
                    </button>
                    <button type="button" class="project-form__file-preview-button project-form__file-preview-button--delete" (click)="deleteVideo()">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 0 1 1.334-1.334h2.666a1.333 1.333 0 0 1 1.334 1.334V4m2 0v9.333a1.333 1.333 0 0 1-1.334 1.334H4.667a1.333 1.333 0 0 1-1.334-1.334V4h9.334Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              }
              }

              <!-- Cover Photo Upload -->
              @if (isFieldVisible('coverPhoto')) {
              <div class="project-form__media-row">
                <div class="project-form__media-info">
                  <h4 class="project-form__media-title">
                    {{ 'settings.project_details_form.cover_photo_title' | translate }}@if (isFieldRequired('coverPhoto')) { <span class="project-form__required">*</span> }
                  </h4>
                  <p class="project-form__media-description">
                    {{ 'settings.project_details_form.cover_photo_description' | translate }}
                  </p>
                </div>
                <div class="project-form__upload-area"
                     (click)="onUploadCoverPhotoClick()"
                     [class.project-form__upload-area--uploading]="isUploadingCoverPhoto">
                  <svg class="project-form__upload-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4V16M12 4L8 8M12 4L16 8M4 17V19C4 19.5304 4.21071 20.0391 4.58579 20.4142C4.96086 20.7893 5.46957 21 6 21H18C18.5304 21 19.0391 20.7893 19.4142 20.4142C19.7893 20.0391 20 19.5304 20 19V17" stroke="#606060" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span class="project-form__upload-text">
                    {{ isUploadingCoverPhoto ? ('settings.project_details_form.uploading' | translate) : ('settings.project_details_form.upload_images_label' | translate) }}
                  </span>
                  <span class="project-form__upload-formats">
                    {{ 'settings.project_details_form.upload_images_formats' | translate }}
                  </span>
                </div>
              </div>

              <input
                #coverPhotoFileInput
                type="file"
                id="coverPhotoFileInput"
                accept=".jpg,.jpeg,.bmp,.png,.jfif,.webp,image/jpeg,image/jpg,image/bmp,image/png,image/jfif,image/webp"
                style="display: none;"
                (change)="onCoverPhotoFileSelected($event)">

              @if (uploadedCoverPhoto) {
                <div class="project-form__file-preview">
                  <div class="project-form__file-preview-content">
                    <svg class="project-form__file-preview-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" stroke="#606060" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M13 2V9H20" stroke="#606060" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span
                      class="project-form__file-preview-name"
                      [customTooltip]="uploadedCoverPhoto.fileName"
                      [tooltipDisabled]="uploadedCoverPhoto.fileName.length <= 80">
                      {{ uploadedCoverPhoto.fileName | truncateFilename }}
                    </span>
                  </div>
                  <div class="project-form__file-preview-actions">
                    <button type="button" class="project-form__file-preview-button project-form__file-preview-button--preview" (click)="previewCoverPhoto()">
                      Preview
                    </button>
                    <button type="button" class="project-form__file-preview-button project-form__file-preview-button--delete" (click)="deleteCoverPhoto()">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 0 1 1.334-1.334h2.666a1.333 1.333 0 0 1 1.334 1.334V4m2 0v9.333a1.333 1.333 0 0 1-1.334 1.334H4.667a1.333 1.333 0 0 1-1.334-1.334V4h9.334Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              }
              }

              <!-- Description -->
              @if (isFieldVisible('description')) {
              <div class="project-form__field">
                <div class="project-form__field-header">
                  <label class="project-form__label" for="description">
                    {{ 'settings.project_details_form.description_label' | translate }}@if (isFieldRequired('description')) { <span class="project-form__required">*</span> }
                  </label>
                  <span class="project-form__char-count">
                    {{ DESCRIPTION_MAX_LENGTH - getCharCount('projectContent.description') }}
                    {{ 'settings.project_details_form.char_limit' | translate }}
                  </span>
                </div>
                <textarea
                  id="description"
                  class="project-form__textarea"
                  formControlName="description"
                  [class.project-form__input--error]="detailsForm.get('projectContent.description')?.invalid && detailsForm.get('projectContent.description')?.touched"
                  [placeholder]="'settings.project_details_form.description_placeholder' | translate"
                  [maxlength]="DESCRIPTION_MAX_LENGTH"
                  rows="4">
                </textarea>
                @if (detailsForm.get('projectContent.description')?.hasError('required') && detailsForm.get('projectContent.description')?.touched) {
                  <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                }
              </div>
              }

              <!-- Problem -->
              @if (isFieldVisible('problem')) {
              <div class="project-form__field">
                <div class="project-form__field-header">
                  <label class="project-form__label" for="problem">
                    {{ 'settings.project_details_form.problem_label' | translate }}@if (isFieldRequired('problem')) { <span class="project-form__required">*</span> }
                  </label>
                  <span class="project-form__char-count">
                    {{ DESCRIPTION_MAX_LENGTH - getCharCount('projectContent.problem') }}
                    {{ 'settings.project_details_form.char_limit' | translate }}
                  </span>
                </div>
                <textarea
                  id="problem"
                  class="project-form__textarea"
                  formControlName="problem"
                  [class.project-form__input--error]="detailsForm.get('projectContent.problem')?.invalid && detailsForm.get('projectContent.problem')?.touched"
                  [placeholder]="'settings.project_details_form.problem_placeholder' | translate"
                  [maxlength]="DESCRIPTION_MAX_LENGTH"
                  rows="4">
                </textarea>
                @if (detailsForm.get('projectContent.problem')?.hasError('required') && detailsForm.get('projectContent.problem')?.touched) {
                  <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                }
              </div>
              }

              <!-- Solutions -->
              @if (isFieldVisible('solutions')) {
              <div class="project-form__field">
                <div class="project-form__field-header">
                  <label class="project-form__label" for="solutions">
                    {{ 'settings.project_details_form.solutions_label' | translate }}@if (isFieldRequired('solutions')) { <span class="project-form__required">*</span> }
                  </label>
                  <span class="project-form__char-count">
                    {{ DESCRIPTION_MAX_LENGTH - getCharCount('projectContent.solutions') }}
                    {{ 'settings.project_details_form.char_limit' | translate }}
                  </span>
                </div>
                <textarea
                  id="solutions"
                  class="project-form__textarea"
                  formControlName="solutions"
                  [class.project-form__input--error]="detailsForm.get('projectContent.solutions')?.invalid && detailsForm.get('projectContent.solutions')?.touched"
                  [placeholder]="'settings.project_details_form.solutions_placeholder' | translate"
                  [maxlength]="DESCRIPTION_MAX_LENGTH"
                  rows="4">
                </textarea>
                @if (detailsForm.get('projectContent.solutions')?.hasError('required') && detailsForm.get('projectContent.solutions')?.touched) {
                  <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                }
              </div>
              }

              <!-- Activities -->
              @if (isFieldVisible('activities')) {
              <div class="project-form__field">
                <div class="project-form__field-header">
                  <label class="project-form__label" for="activities">
                    {{ 'settings.project_details_form.activities_label' | translate }}@if (isFieldRequired('activities')) { <span class="project-form__required">*</span> }
                  </label>
                  <span class="project-form__char-count">
                    {{ DESCRIPTION_MAX_LENGTH - getCharCount('projectContent.activities') }}
                    {{ 'settings.project_details_form.char_limit' | translate }}
                  </span>
                </div>
                <textarea
                  id="activities"
                  class="project-form__textarea"
                  formControlName="activities"
                  [class.project-form__input--error]="detailsForm.get('projectContent.activities')?.invalid && detailsForm.get('projectContent.activities')?.touched"
                  [placeholder]="'settings.project_details_form.activities_placeholder' | translate"
                  [maxlength]="DESCRIPTION_MAX_LENGTH"
                  rows="4">
                </textarea>
                @if (detailsForm.get('projectContent.activities')?.hasError('required') && detailsForm.get('projectContent.activities')?.touched) {
                  <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                }
              </div>
              }
            </div>
            }

          </form>
        }

        <!-- Team Step -->
        @if (!isLoadingDropdowns && currentStep === ProjectFormStep.TEAM) {
          <form [formGroup]="teamForm" class="project-form__form">

            <!-- ── Team Overview Section ── -->
            @if (isSectionVisible('teamOverview')) {
            <div class="project-form__section">
              <ng-container formGroupName="teamOverview">
                <!-- Team Summary -->
                @if (isFieldVisible('teamSummary')) {
                <div class="project-form__field">
                  <div class="project-form__field-header">
                    <label class="project-form__label">
                      {{ 'settings.project_details_form.team_summary_label' | translate }}@if (isFieldRequired('teamSummary')) { <span class="project-form__required">*</span> }
                    </label>
                    <span class="project-form__char-count">
                      {{ DESCRIPTION_MAX_LENGTH - getTeamCharCount('teamOverview.teamSummary') }}
                      {{ 'settings.project_details_form.char_limit' | translate }}
                    </span>
                  </div>
                  <textarea
                    class="project-form__textarea"
                    formControlName="teamSummary"
                    [class.project-form__input--error]="teamForm.get('teamOverview.teamSummary')?.invalid && teamForm.get('teamOverview.teamSummary')?.touched"
                    [maxlength]="DESCRIPTION_MAX_LENGTH"
                    [placeholder]="'settings.project_details_form.team_summary_placeholder' | translate"
                    rows="4">
                  </textarea>
                  @if (teamForm.get('teamOverview.teamSummary')?.hasError('required') && teamForm.get('teamOverview.teamSummary')?.touched) {
                    <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                  }
                </div>
                }

                <!-- Project Manager -->
                @if (isFieldVisible('projectManager')) {
                <div class="project-form__field">
                  <label class="project-form__label">
                    {{ 'settings.project_details_form.project_manager_label' | translate }}@if (isFieldRequired('projectManager')) { <span class="project-form__required">*</span> }
                  </label>
                  <div class="project-form__select-wrapper">
                    <select
                      class="project-form__select"
                      formControlName="projectManager"
                      [class.project-form__input--error]="teamForm.get('teamOverview.projectManager')?.invalid && teamForm.get('teamOverview.projectManager')?.touched">
                      <option [ngValue]="null" disabled hidden>{{ projectManagers.length === 0 ? ('settings.project_details_form.no_project_managers' | translate) : ('settings.project_details_form.project_manager_placeholder' | translate) }}</option>
                      @for (manager of projectManagers; track manager.uuid) {
                        <option [ngValue]="manager.uuid">{{ manager.name }}</option>
                      }
                    </select>
                    @if (teamForm.get('teamOverview.projectManager')?.value) {
                      <button type="button" class="project-form__select-clear-btn" (click)="clearProjectManager()">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none">
                          <path d="M0.667969 3.33203H12.668" stroke="#96080B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M11.3372 3.33203V12.6654C11.3372 13.332 10.6706 13.9987 10.0039 13.9987H3.33724C2.67057 13.9987 2.00391 13.332 2.00391 12.6654V3.33203" stroke="#96080B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M4.00391 3.33268V1.99935C4.00391 1.33268 4.67057 0.666016 5.33724 0.666016H8.00391C8.67057 0.666016 9.33724 1.33268 9.33724 1.99935V3.33268" stroke="#96080B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M5.33203 6.66602V10.666" stroke="#96080B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M8.00391 6.66602V10.666" stroke="#96080B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </button>
                    }
                  </div>
                  @if (teamForm.get('teamOverview.projectManager')?.hasError('required') && teamForm.get('teamOverview.projectManager')?.touched) {
                    <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                  }
                </div>
                }
              </ng-container>
            </div>
            }

            <!-- ── Team Members Section ── -->
            @if (isSectionVisible('teamMember')) {
            <div class="project-form__section">
                <div class="project-form__section-header">
                  <h3 class="project-form__section-title">
                    {{ 'settings.project_details_form.team_members_title' | translate }}
                  </h3>
                </div>

              <!-- Committed team members -->
              <div formArrayName="teamMembers">
                @for (member of teamMembers.controls; track $index; let i = $index) {
                  <app-team-member-card
                    [memberForm]="getTeamMemberForm(i)"
                    [photoUrl]="teamMemberPhotoUrls[i] || ''"
                    [showRemoveButton]="true"
                    [nameTitleRequired]="isFieldRequired('nameTitle')"
                    [emailRequired]="isFieldRequired('email')"
                    [bioRequired]="isFieldRequired('bio')"
                    [nameTitleVisible]="isFieldVisible('nameTitle')"
                    [emailVisible]="isFieldVisible('email')"
                    [bioVisible]="isFieldVisible('bio')"
                    (photoFileSelected)="onTeamMemberPhotoFile(i, $event)"
                    (remove)="removeTeamMember(i)">
                  </app-team-member-card>
                }
              </div>

              <!-- Add member form: always visible at the bottom -->
              <app-team-member-card
                [memberForm]="addMemberForm"
                [photoUrl]="addMemberPhotoUrl"
                [cardTitle]="'settings.project_details_form.add_team_member' | translate"
                [showCommitButton]="true"
                [nameTitleRequired]="isFieldRequired('nameTitle')"
                [emailRequired]="isFieldRequired('email')"
                [bioRequired]="isFieldRequired('bio')"
                [nameTitleVisible]="isFieldVisible('nameTitle')"
                [emailVisible]="isFieldVisible('email')"
                [bioVisible]="isFieldVisible('bio')"
                (photoFileSelected)="onAddMemberPhotoFile($event)"
                (commit)="commitNewTeamMember()">
              </app-team-member-card>
            </div>
            }

            <!-- ── Partner Organizations Section ── -->
            @if (isSectionVisible('partnerOrg')) {
            <div class="project-form__section">
                <div class="project-form__section-header">
                  <h3 class="project-form__section-title">
                    {{ 'settings.project_details_form.partner_orgs_title' | translate }}
                  </h3>
                  <p class="project-form__section-description">
                    {{ 'settings.project_details_form.partner_org_section_description' | translate }}
                  </p>
                </div>

              <!-- Committed partner organizations -->
              <div formArrayName="partnerOrganizations">
                @for (org of partnerOrganizations.controls; track $index; let i = $index) {
                  <div class="project-form__partner-org-row">
                    <span class="project-form__partner-org-row-name">{{ org.get('name')?.value }}</span>
                    <span class="project-form__partner-org-row-desc">{{ org.get('description')?.value }}</span>
                    <div class="project-form__partner-org-menu-wrapper">
                      <button
                        type="button"
                        class="project-form__partner-org-kebab"
                        (click)="togglePartnerOrgMenu($event, i)"
                        [attr.aria-label]="'settings.project_details_form.partner_org_actions' | translate">
                        &#8942;
                      </button>
                      @if (activePartnerOrgMenuIndex === i) {
                        <div class="project-form__partner-org-menu">
                          <button type="button" class="project-form__partner-org-menu-item" (click)="editPartnerOrg(i)">
                            {{ 'settings.project_details_form.edit_partner_org' | translate }}
                          </button>
                          <button type="button" class="project-form__partner-org-menu-item project-form__partner-org-menu-item--danger" (click)="removePartnerOrg(i)">
                            {{ 'settings.project_details_form.remove_partner_org' | translate }}
                          </button>
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>

              <!-- Add / Edit partner org form: always visible at the bottom -->
              <div class="project-form__partner-org-card project-form__partner-org-card--add" [formGroup]="addPartnerOrgForm">
                  <h4 class="project-form__partner-org-card-title">
                    @if (editingPartnerOrgIndex !== null) {
                      {{ 'settings.project_details_form.edit_partner_org_title' | translate }}
                    } @else {
                      {{ 'settings.project_details_form.add_partner_org' | translate }}
                    }
                  </h4>
                  <div class="project-form__field-row project-form__partner-org-fields">
                    @if (isFieldVisible('name')) {
                      <div class="project-form__field">
                        <label class="project-form__label">
                          {{ 'settings.project_details_form.partner_org_name_label' | translate }}@if (isFieldRequired('name')) { <span class="project-form__required">*</span> }
                        </label>
                        <div class="project-form__charity-wrapper">
                          <input
                            type="text"
                            class="project-form__input"
                            [class.project-form__input--error]="addPartnerOrgForm.get('name')?.invalid && addPartnerOrgForm.get('name')?.touched"
                            [value]="partnerOrgDisplayValue"
                            (input)="onPartnerOrgInputChange($any($event.target).value)"
                            [placeholder]="'settings.project_details_form.partner_org_name_placeholder' | translate"
                            autocomplete="off">
                          @if (partnerOrgSearchResults.length > 0) {
                            <div class="project-form__charity-dropdown">
                              @for (org of partnerOrgSearchResults; track org.uuid) {
                                <div class="project-form__charity-dropdown-item" (click)="onPartnerOrgSelected(org)">
                                  {{ org.name }}
                                </div>
                              }
                            </div>
                          }
                        </div>
                        @if (addPartnerOrgForm.get('name')?.hasError('required') && addPartnerOrgForm.get('name')?.touched) {
                          <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                        }
                      </div>
                    }
                    @if (isFieldVisibleByKey('relationshipDescription')) {
                    <div class="project-form__field">
                      <label class="project-form__label">
                        {{ 'settings.project_details_form.partner_org_description_label' | translate }}@if (isFieldRequiredByKey('relationshipDescription')) { <span class="project-form__required">*</span> }
                      </label>
                      <input
                        type="text"
                        class="project-form__input"
                        formControlName="description"
                        maxlength="500"
                        [class.project-form__input--error]="addPartnerOrgForm.get('description')?.invalid && addPartnerOrgForm.get('description')?.touched"
                        [placeholder]="'settings.project_details_form.partner_org_description_placeholder' | translate">
                      @if (addPartnerOrgForm.get('description')?.hasError('required') && addPartnerOrgForm.get('description')?.touched) {
                        <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                      }
                    </div>
                    }
                  </div>
                  <div class="project-form__team-commit-row">
                    @if (editingPartnerOrgIndex !== null) {
                      <button
                        type="button"
                        class="project-form__team-commit-btn project-form__team-commit-btn--secondary"
                        (click)="cancelEditPartnerOrg()">
                        {{ 'settings.project_details_form.cancel_edit_partner_org' | translate }}
                      </button>
                    }
                    <button
                      type="button"
                      class="project-form__team-commit-btn"
                      (click)="commitNewPartnerOrg()">
                      @if (editingPartnerOrgIndex !== null) {
                        {{ 'settings.project_details_form.save_partner_org' | translate }}
                      } @else {
                        {{ 'settings.project_details_form.add_partner_org_confirm' | translate }}
                      }
                    </button>
                  </div>
                </div>

            </div>
            }

          </form>
        }

        <!-- Goals Step -->
        @if (!isLoadingDropdowns && currentStep === ProjectFormStep.GOALS) {
          <form [formGroup]="goalsForm" class="project-form__form">

            <!-- ── Goals Overview Section ── -->
            @if (isSectionVisible('goalsOverview')) {
            <div class="project-form__section">
              <ng-container formGroupName="goalsOverview">

                <!-- Results Measurement -->
                @if (isFieldVisible('resultsMeasurement')) {
                <div class="project-form__field">
                  <div class="project-form__field-header">
                    <label class="project-form__label">
                      {{ 'settings.project_details_form.results_measurement_label' | translate }}@if (isFieldRequired('resultsMeasurement')) { <span class="project-form__required">*</span> }
                    </label>
                    <span class="project-form__char-count">
                      {{ DESCRIPTION_MAX_LENGTH - getGoalsCharCount('goalsOverview.resultsMeasurement') }}
                      {{ 'settings.project_details_form.char_limit' | translate }}
                    </span>
                  </div>
                  <textarea
                    class="project-form__textarea"
                    formControlName="resultsMeasurement"
                    [class.project-form__input--error]="goalsForm.get('goalsOverview.resultsMeasurement')?.invalid && goalsForm.get('goalsOverview.resultsMeasurement')?.touched"
                    [maxlength]="DESCRIPTION_MAX_LENGTH"
                    [placeholder]="'settings.project_details_form.results_measurement_placeholder' | translate"
                    rows="4">
                  </textarea>
                  @if (goalsForm.get('goalsOverview.resultsMeasurement')?.hasError('required') && goalsForm.get('goalsOverview.resultsMeasurement')?.touched) {
                    <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
                  }
                </div>
                }

              </ng-container>
            </div>
            }

          </form>
        }

        <!-- Gallery Step -->
        @if (!isLoadingDropdowns && currentStep === ProjectFormStep.GALLERY) {
          <div class="project-form__gallery">

            <!-- Upload Zone Label -->
            <label class="project-form__label">
              {{ 'settings.project_details_form.gallery_add_label' | translate }}
              @if (isFieldRequiredByKey('projectImages')) {
                <span class="project-form__required">*</span>
              }
            </label>

            <app-gallery-upload-zone
              [entityUuid]="projectUuid"
              [initialFiles]="galleryFiles"
              (filesChange)="onGalleryFilesChange($event)"
              (entityUuidChange)="projectUuid = $event">
            </app-gallery-upload-zone>

            @if (showGalleryImageError) {
              <div class="project-form__error">{{ 'settings.project_details_form.required_field' | translate }}</div>
            }

          </div>
        }
      </div>

      <!-- Footer Bar -->
      <div class="project-form__footer">
        <button
          class="project-form__footer-button project-form__footer-button--back"
          type="button"
          (click)="onBack()"
          [disabled]="isBackDisabled()">
          {{ 'settings.project_details_form.back_button' | translate }}
        </button>
        <button
          class="project-form__footer-button project-form__footer-button--next"
          type="button"
          [disabled]="isNextDisabled()"
          (click)="onNext()">
          {{ isSaving ? ('settings.project_details_form.saving' | translate) : ('settings.project_details_form.next_button' | translate) }}
        </button>
      </div>
    </div>
  </div>

  <!-- Mobile Footer -->
  <div class="project-form__mobile-footer">
    <div class="project-form__mobile-footer-buttons">
      @if (showSaveAsDraftButton) {
        <button
          class="project-form__button project-form__button--draft"
          type="button"
          [disabled]="isSaving"
          (click)="onSaveAsDraft()">
          {{ isSaving ? ('settings.project_details_form.saving' | translate) : ('settings.project_details_form.save_as_draft' | translate) }}
        </button>
      }
      <button
        class="project-form__button project-form__button--submit"
        type="button"
        [disabled]="isSubmitting || isCharityBlocked"
        (click)="onSubmit()">
        {{ isSubmitting ? ('settings.project_details_form.submitting' | translate) : ('settings.project_details_form.submit' | translate) }}
      </button>
    </div>
    @if (charityBlockedCase) {
      <div class="project-form__charity-blocked-hint">
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" class="project-form__charity-blocked-hint-icon">
          <g clip-path="url(#clip0_charity_blocked_hint_mobile)">
            <path d="M6.5 12.25C9.67564 12.25 12.25 9.67564 12.25 6.5C12.25 3.32436 9.67564 0.75 6.5 0.75C3.32436 0.75 0.75 3.32436 0.75 6.5C0.75 9.67564 3.32436 12.25 6.5 12.25Z" stroke="#E91A1A" stroke-width="1.2"/>
            <path d="M6.5 5.5V9" stroke="#E91A1A" stroke-width="1.2" stroke-linecap="round"/>
            <path d="M6.50039 4.79998C6.83176 4.79998 7.10039 4.53135 7.10039 4.19998C7.10039 3.8686 6.83176 3.59998 6.50039 3.59998C6.16902 3.59998 5.90039 3.8686 5.90039 4.19998C5.90039 4.53135 6.16902 4.79998 6.50039 4.79998Z" fill="#E91A1A"/>
          </g>
          <defs>
            <clipPath id="clip0_charity_blocked_hint_mobile">
              <rect width="13" height="13" fill="white"/>
            </clipPath>
          </defs>
        </svg>
        <span [innerHTML]="'settings.project_details_form.charity_not_live_submit_hint' | translate"></span>
      </div>
    }
  </div>
</div>
